import React, { type FC, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  Checkbox,
  Flex,
  Grid,
  GridCell,
  LINK_VARIANT_HIGHLIGHT,
  Link,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  TEXT_VARIANT_BOLD,
  Text,
  TruncateAndTooltip,
} from 'mailup-web-components'

import { isNullOrEmpty } from 'Helpers/strings'

import {
  type GroupItem,
  type GroupStats,
  groupTypeLabelMap,
} from 'Pages/GroupsList/model'
import { toggleItemSelectionCallback } from '../recoil/callbacks/callbacks'
import { groupsStatsState, selectedItemState } from '../recoil/state/state'
import { getRecipientsListUrlToNavigate } from '../utils/statUtil'

interface ResultItemProps {
  cols: number
  spans: number[]
  item: GroupItem
  onEdit: () => void
}

const ResultItem: FC<ResultItemProps> = ({ cols, spans, item, onEdit }) => {
  const intl = useIntl()
  const navigate = useNavigate()

  const { stats: allStats, loading: statsLoading } =
    useRecoilValue(groupsStatsState)

  const stat = allStats.find((s) => s.IdGroup === item.Id)

  const statCol = (
    statProp: keyof GroupStats,
    index: number,
  ): React.JSX.Element => {
    const value = stat?.[statProp]
    return (
      <GridCell span={spans[index]}>
        <Flex justifyContent="right">
          {statsLoading && (
            <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
          )}
          {!statsLoading && value === undefined && ' '}
          {!statsLoading && value === 0 && intl.formatNumber(value)}
          {!statsLoading && value !== undefined && value > 0 && (
            <Link
              variant={LINK_VARIANT_HIGHLIGHT}
              data-mu-test={`group__stats-${statProp}`}
              onClick={() => {
                onClickStat(statProp)
              }}
            >
              {intl.formatNumber(value)}
            </Link>
          )}
        </Flex>
      </GridCell>
    )
  }

  const onClickStat = useCallback(
    (statProp: keyof GroupStats) => {
      const idGroup = item.Id
      navigate(getRecipientsListUrlToNavigate(statProp, idGroup))
    },
    [item.Id, navigate],
  )

  const checked = useRecoilValue(selectedItemState(item.Id))
  const toggleItem = useRecoilCallback(toggleItemSelectionCallback, [])

  const handleToggle = useCallback(() => {
    void toggleItem(item)
  }, [toggleItem, item])

  return (
    <Grid cols={cols}>
      <GridCell span={spans[0]}>
        <Checkbox
          checked={checked}
          data-mu-test="select-all"
          onChange={handleToggle}
          disabled={!item.Deleteable}
        />
      </GridCell>
      <GridCell span={spans[1]}>{item.Id}</GridCell>
      <GridCell span={spans[2]}>
        <Flex flexDirection="column" gap="8px">
          {item.Deleteable ? (
            <Link
              onClick={onEdit}
              variant={LINK_VARIANT_HIGHLIGHT}
              data-mu-test="group__name"
            >
              <TruncateAndTooltip text={item.Name} />
            </Link>
          ) : (
            <Text variant={TEXT_VARIANT_BOLD} data-mu-test="group__name">
              <TruncateAndTooltip text={item.Name} />
            </Text>
          )}

          {!isNullOrEmpty(item.Notes) && (
            <TruncateAndTooltip text={item.Notes} />
          )}
        </Flex>
      </GridCell>
      <GridCell span={spans[3]}>
        {intl.formatMessage({ id: groupTypeLabelMap[item.Type] })}
      </GridCell>
      {statCol('EmailSubscribed', 4)}
      {statCol('EmailUnsubscribed', 5)}
      {statCol('EmailPending', 6)}
      {statCol('SmsSubscribed', 7)}
      {statCol('SmsUnsubscribed', 8)}
    </Grid>
  )
}

export default ResultItem
